import { useLayoutEffect, useSyncExternalStore } from "react";

const blockers = new Set<symbol>();
const listeners = new Set<() => void>();
let registrationPromise: Promise<ServiceWorkerRegistration> | undefined;
let waitingWorker: ServiceWorker | null = null;
let reloadRequired = false;
let offlineReady = false;
let updateState: "idle" | "checking" | "blocked" | "failed" = "idle";
let installPrompt: InstallPromptEvent | null = null;
let installed = false;
let started = false;
let revision = 0;

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function emit() {
  revision += 1;
  listeners.forEach((listener) => listener());
}

export function useUpdateBlocker(blocked: boolean) {
  useLayoutEffect(() => {
    if (!blocked) return;
    const key = Symbol();
    blockers.add(key);
    return () => {
      blockers.delete(key);
    };
  }, [blocked]);
}

export function isUpdateBlocked() {
  return blockers.size > 0;
}

export async function registerAdminWorker(): Promise<ServiceWorkerRegistration> {
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(import.meta.env.DEV ? "/dev-sw.js?dev-sw" : "/admin-sw.js", {
        scope: import.meta.env.DEV ? "/admin-push/" : "/",
        type: import.meta.env.DEV ? "module" : "classic",
        updateViaCache: "none",
      })
      .catch((error) => {
        registrationPromise = undefined;
        throw error;
      });
  }
  return registrationPromise;
}

export function startAdminPwa() {
  if (started) return;
  started = true;
  const display = window.matchMedia("(display-mode: standalone)");
  const detectInstalled = () => {
    installed =
      display.matches ||
      Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
    emit();
  };
  detectInstalled();
  display.addEventListener("change", detectInstalled);
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    installPrompt = event as InstallPromptEvent;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    installed = true;
    installPrompt = null;
    emit();
  });
  if (!("serviceWorker" in navigator)) return;
  // Development uses MSW at the root scope; its activation is not offline readiness.
  if (!import.meta.env.PROD) return;
  let hadController = Boolean(navigator.serviceWorker.controller);

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "ADMIN_UPDATE_CHECK") {
      event.ports[0]?.postMessage({ safe: !isUpdateBlocked() });
    }
  });
  // Each tab decides when it is safe to reload, even if another tab applies an update.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController) {
      hadController = true;
      offlineReady = true;
      emit();
      return;
    }
    if (!isUpdateBlocked()) window.location.reload();
    else {
      reloadRequired = true;
      updateState = "blocked";
      emit();
    }
  });

  const register = async () => {
    try {
      const registration = await registerAdminWorker();
      offlineReady = Boolean(registration.active);
      const checkWaiting = () => {
        // A first install briefly enters "waiting" before activation. It is
        // offline preparation, not an update to the currently running app.
        waitingWorker = navigator.serviceWorker.controller
          ? registration.waiting
          : null;
        emit();
      };
      checkWaiting();
      registration.addEventListener("updatefound", () => {
        const worker = registration.installing;
        worker?.addEventListener("statechange", () => {
          if (worker.state === "installed") {
            offlineReady = true;
            checkWaiting();
          }
          if (worker.state === "activated") checkWaiting();
        });
      });
      const checkUpdate = () => {
        if (navigator.onLine && document.visibilityState === "visible") {
          void registration.update().catch(() => undefined);
        }
      };
      window.addEventListener("online", checkUpdate);
      document.addEventListener("visibilitychange", checkUpdate);
    } catch {
      updateState = "failed";
      emit();
    }
  };
  if (document.readyState === "complete") void register();
  else
    window.addEventListener(
      "load",
      () => {
        void register();
      },
      { once: true },
    );
}

export async function installAdminPwa() {
  const prompt = installPrompt;
  if (!prompt) return;
  installPrompt = null;
  emit();
  try {
    await prompt.prompt();
    await prompt.userChoice;
  } finally {
    emit();
  }
}

export async function applyAdminUpdate() {
  if (updateState === "checking" || (!waitingWorker && !reloadRequired)) return;
  if (isUpdateBlocked()) {
    updateState = "blocked";
    emit();
    return;
  }
  if (reloadRequired) {
    window.location.reload();
    return;
  }
  updateState = "checking";
  emit();
  const worker = waitingWorker!;
  const safe = await new Promise<boolean>((resolve) => {
    const channel = new MessageChannel();
    const finish = (result: boolean) => {
      window.clearTimeout(timer);
      channel.port1.close();
      resolve(result);
    };
    const timer = window.setTimeout(() => finish(false), 5000);
    channel.port1.onmessage = (event) => finish(event.data?.accepted === true);
    worker.postMessage({ type: "ADMIN_APPLY_UPDATE" }, [channel.port2]);
  });
  updateState = safe ? "idle" : "blocked";
  emit();
  if (safe && worker.state === "activated" && !isUpdateBlocked())
    window.location.reload();
}

export function useAdminPwa() {
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    () => revision,
  );
  return {
    canInstall: Boolean(installPrompt),
    installed,
    offlineReady,
    updateAvailable: Boolean(waitingWorker) || reloadRequired,
    updateState,
  };
}

export function useOnline() {
  return useSyncExternalStore(
    (listener) => {
      window.addEventListener("online", listener);
      window.addEventListener("offline", listener);
      return () => {
        window.removeEventListener("online", listener);
        window.removeEventListener("offline", listener);
      };
    },
    () => navigator.onLine,
  );
}
